// Tabela de métricas por página do Sentry

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText } from 'lucide-react';
import { 
  getMetricStatus, 
  formatMetricValue, 
  STATUS_CONFIG,
  type PageMetric 
} from '@/types/performance';

interface PagesTableSentryProps {
  pages: PageMetric[];
  isLoading: boolean;
}

function MetricBadge({ metricKey, value }: { metricKey: 'lcp' | 'fcp' | 'cls' | 'ttfb'; value: number | null }) {
  const status = getMetricStatus(metricKey, value);
  const statusConfig = STATUS_CONFIG[status];
  
  return (
    <Badge 
      variant="outline" 
      className={`font-mono ${statusConfig.bgColor} ${statusConfig.borderColor} ${statusConfig.textColor}`}
    >
      {formatMetricValue(metricKey, value)}
    </Badge>
  );
}

export function PagesTableSentry({ pages, isLoading }: PagesTableSentryProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Desempenho por Página
          </CardTitle>
          <CardDescription>Top 10 páginas por tempo de LCP</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-lg border">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-12" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Desempenho por Página
          </CardTitle>
          <CardDescription>Top 10 páginas por tempo de LCP</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Sem dados de páginas no período selecionado
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Desempenho por Página
        </CardTitle>
        <CardDescription>Top 10 páginas por tempo de LCP</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <th className="text-left py-3 font-medium">Página</th>
                <th className="text-center py-3 font-medium">LCP</th>
                <th className="text-center py-3 font-medium">FCP</th>
                <th className="text-center py-3 font-medium">CLS</th>
                <th className="text-center py-3 font-medium">TTFB</th>
                <th className="text-right py-3 font-medium">Amostras</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page, index) => (
                <tr key={page.path || index} className="border-b hover:bg-muted/50">
                  <td className="py-3 pr-4">
                    <span className="font-mono text-xs truncate block max-w-[200px]" title={page.path}>
                      {page.path || '-'}
                    </span>
                  </td>
                  <td className="text-center py-3">
                    <MetricBadge metricKey="lcp" value={page.lcp} />
                  </td>
                  <td className="text-center py-3">
                    <MetricBadge metricKey="fcp" value={page.fcp} />
                  </td>
                  <td className="text-center py-3">
                    <MetricBadge metricKey="cls" value={page.cls} />
                  </td>
                  <td className="text-center py-3">
                    <MetricBadge metricKey="ttfb" value={page.ttfb} />
                  </td>
                  <td className="text-right py-3 text-muted-foreground">
                    {page.samples.toLocaleString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
